import { useEffect } from "react"

/**
 * Custom hook to perform an action when specific keys are pressed.
 *
 * @param {UseKeyPressProps} props The properties for the hook.
 * @param {string[]} props.targetKeys Array of keys that trigger the action when pressed.
 * @param {() => void} props.action The action to perform when a target key is pressed.
 *
 * This hook adds an event listener for keyboard "keydown" events and triggers
 * a specified action if the pressed key matches any in the `targetKeys` array.
 * Useful for handling keyboard shortcuts or navigation.
 *
 * Usage example:
 *
 * useKeyPress({
 *   targetKeys: ['Enter', ' '], // Trigger action on Enter or Space key
 *   action: () => console.log('Key pressed!'),
 * });
 */

type UseKeyPressProps = {
    targetKeys: string[]
    action: () => void
}

export const useKeyPress = ({ targetKeys, action }: UseKeyPressProps) => {
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (targetKeys.includes(event.key)) {
                action()
            }
        }

        // Add event listener
        document.addEventListener("keydown", handleKeyDown)

        // Cleanup
        return () => {
            document.removeEventListener("keydown", handleKeyDown)
        }
    }, [targetKeys, action])
}
